/**
 * Sino de notificacoes in-app.
 *
 * - Faz polling do badge a cada 60s (apenas conta — barato)
 * - Carrega lista quando dropdown abre
 * - Click numa notificacao: marca lida + navega
 * - "Marcar todas como lidas" zera contagem
 *
 * Sem websocket: polling 60s eh "tempo real o suficiente" pra esta dor.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { meAPI } from '../services/inovacaoAPI';

const POLL_MS = 60_000;

export default function NotificacoesBell() {
  const [count, setCount] = useState(0);
  const [aberto, setAberto] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Polling do badge
  useEffect(() => {
    let cancelado = false;
    function atualizar() {
      meAPI.contagemNaoLidas()
        .then((r) => { if (!cancelado) setCount(r.total || 0); })
        .catch(() => {}); // silencioso — sem acesso ao modulo nao quebra UI
    }
    atualizar();
    const id = setInterval(atualizar, POLL_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, []);

  // Fecha ao clicar fora
  useEffect(() => {
    function fora(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setAberto(false);
    }
    if (aberto) document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  function alternar() {
    if (!aberto) {
      setLoading(true);
      meAPI.notificacoes(false)
        .then(setItems)
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }
    setAberto((x) => !x);
  }

  async function ao_clicar(n) {
    if (!n.lida_em) {
      try {
        await meAPI.marcarLida(n.id);
        setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, lida_em: new Date().toISOString() } : x));
        setCount((c) => Math.max(0, c - 1));
      } catch { /* noop */ }
    }
    if (n.link) {
      setAberto(false);
      navigate(n.link);
    }
  }

  async function marcarTodas() {
    try {
      await meAPI.marcarTodasLidas();
      const agora = new Date().toISOString();
      setItems((arr) => arr.map((x) => x.lida_em ? x : { ...x, lida_em: agora }));
      setCount(0);
    } catch { /* noop */ }
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button onClick={alternar}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                       position: 'relative', padding: 6, color: '#fff',
                       display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              title="Notificações">
        <Bell size={20} />
        {count > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            background: '#e74c3c', color: '#fff',
            borderRadius: 999, minWidth: 16, height: 16,
            fontSize: '0.65rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
          }}>{count > 99 ? '99+' : count}</span>
        )}
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '110%', right: 0,
          width: 360, maxHeight: 480, overflowY: 'auto',
          background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          borderRadius: 8, zIndex: 1000, color: '#2c3e50',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '12px 14px',
                        borderBottom: '1px solid #ecf0f1', fontSize: '0.9rem' }}>
            <b style={{ color: '#2c3e50' }}>Notificações</b>
            {count > 0 && (
              <button onClick={marcarTodas}
                      style={{ background: 'transparent', border: 'none', color: 'var(--brand, #20a490)',
                               cursor: 'pointer', fontSize: '0.78rem',
                               display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CheckCheck size={12} /> Marcar todas como lidas
              </button>
            )}
          </div>
          {loading ? (
            <p style={{ padding: 16, color: '#bdc3c7', fontSize: '0.85rem', margin: 0 }}>Carregando…</p>
          ) : items.length === 0 ? (
            <p style={{ padding: 24, color: '#bdc3c7', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>
              Sem notificações.
            </p>
          ) : (
            items.map((n) => (
              <div key={n.id} onClick={() => ao_clicar(n)}
                   style={{ padding: '10px 14px', borderBottom: '1px solid #ecf0f1',
                            cursor: 'pointer', background: n.lida_em ? '#fff' : '#f0faf8' }}
                   onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                   onMouseLeave={(e) => e.currentTarget.style.background = n.lida_em ? '#fff' : '#f0faf8'}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {!n.lida_em && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%',
                                   background: 'var(--brand, #20a490)', marginTop: 6, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: n.lida_em ? 400 : 600,
                                  color: '#2c3e50' }}>{n.titulo}</div>
                    {n.descricao && (
                      <div style={{ fontSize: '0.78rem', color: '#7f8c8d', marginTop: 2,
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap' }}>{n.descricao}</div>
                    )}
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6', marginTop: 4 }}>
                      {tempoRelativo(n.criado_em)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function tempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `há ${Math.floor(diff / 86400)} dia(s)`;
  return d.toLocaleDateString('pt-BR');
}
